import { useTranslation } from 'react-i18next';
import DropdownSelect from 'src/ui/wf/common/select/DropdownSelect';
import Input from '../input/Input';
import Button from '../button/Button';
import { FormRow } from '../form/FormRow';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import { useMapSearch } from './components/useMapSearch';
import { WfOperationStatus } from 'src/api/wf/models/enums/WfOperationtStatus';
import { WfReservedOptions } from 'src/api/wf/stores/WfFilterStore';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import clsx from 'clsx';

interface WF_MapSearchHeaderProps {
  onSearch: () => void;
  showNoResults?: boolean;
}

function WF_MapSearchHeader({ onSearch, showNoResults = true }: WF_MapSearchHeaderProps) {
  const { t } = useTranslation();
  const { availableWorkplaces } = useWfWorkplaceStore();
  const userAgent = useUserAgent();
  const {
    filterParameters,
    errors,
    hasSearched,
    dropdownOptions,
    handleFieldChange,
    handleSearch,
  } = useMapSearch(onSearch);

  const initialOption = { title: t('wf.common.none'), value: '' };
  const mobileInlineRowClassName = !userAgent.isDesktop ? 'flex-row items-center gap-2' : '';
  const mobileInlineTitleClassName = !userAgent.isDesktop ? 'mb-0 w-24 shrink-0' : '';
  const mobileFieldClassName = !userAgent.isDesktop
    ? 'w-full min-w-0 border-2 bg-white px-1 py-px text-sm text-black shadow-inner border-b-gray-300 border-l-gray-700 border-r-gray-300 border-t-gray-700'
    : '';
  const mobileInputClassName = !userAgent.isDesktop
    ? 'w-full min-w-0 border border-gray-500 px-1'
    : '';
  const mobileDropdownClassProps = !userAgent.isDesktop ? { className: mobileFieldClassName } : {};
  const mobileInputClassProps = !userAgent.isDesktop ? { className: mobileInputClassName } : {};

  return (
    <div>
      <div
        className={clsx(
          'rounded-md border-t-2 border-sky-600 bg-gray-100',
          'flex',
          userAgent.isDesktop
            ? 'flex-col gap-y-4 p-3 min-[1150px]:flex-row min-[1150px]:items-start'
            : 'flex-row px-2 pt-2'
        )}
      >
        <div
          className={clsx('flex-1 space-y-2', {
            'min-[1150px]:pr-4': userAgent.isDesktop,
            'mr-2 min-w-0': !userAgent.isDesktop,
          })}
        >
          {/* --- Location Search Section --- */}
          <div
            className={clsx(
              userAgent.isDesktop
                ? 'grid grid-cols-1 gap-x-4 gap-y-2 min-[850px]:grid-cols-2 min-[1460px]:grid-cols-3'
                : 'space-y-1 px-2'
            )}
          >
            <div
              className={clsx(
                'font-semibold',
                userAgent.isDesktop ? 'col-span-full text-left' : 'text-center'
              )}
            >
              {t('wf.searchHeader.locationSearch')}
            </div>
            <FormRow
              title={t('wf.common.branchOffice')}
              required
              showError={!!errors.branch}
              errorMessage={errors.branch ?? ''}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.branch}
                selectedValue={
                  dropdownOptions.branch.find((opt) => opt.value === filterParameters.branch) ??
                  initialOption
                }
                onChange={(e) => handleFieldChange('branch', e.target.value)}
                placeholder={t('wf.common.none')}
              />
            </FormRow>

            <FormRow
              title={t('wf.common.salesOffice')}
              required
              showError={!!errors.officeOrCustomer}
              errorMessage={errors.officeOrCustomer ?? ''}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.salesOffice}
                selectedValue={
                  dropdownOptions.salesOffice.find(
                    (opt) => opt.value === filterParameters.salesOffice
                  ) ?? initialOption
                }
                onChange={(e) => handleFieldChange('salesOffice', e.target.value)}
                placeholder={t('wf.common.none')}
              />
            </FormRow>

            <FormRow
              title={t('wf.common.section')}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.section}
                selectedValue={
                  dropdownOptions.section.find((opt) => opt.value === filterParameters.section) ??
                  initialOption
                }
                onChange={(e) => handleFieldChange('section', e.target.value)}
                placeholder={t('wf.common.none')}
              />
            </FormRow>

            <FormRow
              title={t('wf.common.squad')}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.squad}
                selectedValue={
                  dropdownOptions.squad.find((opt) => opt.value === filterParameters.squad) ??
                  initialOption
                }
                onChange={(e) => handleFieldChange('squad', e.target.value)}
                placeholder={t('wf.common.none')}
              />
            </FormRow>

            <FormRow
              title={t('wf.searchHeader.reservation')}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.reserved}
                selectedValue={
                  dropdownOptions.reserved.find((opt) => opt.value === filterParameters.reserved) ??
                  dropdownOptions.reserved[0]
                }
                onChange={(e) => handleFieldChange('reserved', e.target.value as WfReservedOptions)}
              />
            </FormRow>

            <FormRow
              title={t('wf.common.status')}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.status}
                selectedValue={
                  dropdownOptions.status.find((opt) => opt.value === filterParameters.statusList) ??
                  initialOption
                }
                onChange={(e) =>
                  handleFieldChange('statusList', e.target.value as WfOperationStatus)
                }
              />
            </FormRow>
          </div>

          <div role="separator" aria-orientation="horizontal" className="h-px w-full bg-gray-400" />

          {/* --- Customer Search Section --- */}
          <div
            className={clsx(
              userAgent.isDesktop
                ? 'grid grid-cols-1 gap-x-4 gap-y-2 min-[850px]:grid-cols-2 min-[1460px]:grid-cols-3'
                : 'space-y-1 px-2'
            )}
          >
            <div
              className={clsx(
                'font-semibold',
                userAgent.isDesktop ? 'col-span-full text-left' : 'text-center'
              )}
            >
              {t('wf.searchHeader.customerSearch')}
            </div>
            <FormRow
              title={t('wf.common.branchOffice')}
              required
              showError={!!errors.branch}
              errorMessage={errors.branch ?? ''}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <DropdownSelect
                {...mobileDropdownClassProps}
                optionValues={dropdownOptions.branch}
                selectedValue={
                  dropdownOptions.branch.find((opt) => opt.value === filterParameters.branch) ??
                  initialOption
                }
                onChange={(e) => handleFieldChange('branch', e.target.value)}
                placeholder={t('wf.common.none')}
              />
            </FormRow>

            <FormRow
              title={t('wf.common.customerNumber')}
              required
              showError={!!errors.officeOrCustomer}
              errorMessage={errors.officeOrCustomer ?? ''}
              className={mobileInlineRowClassName}
              titleClassName={mobileInlineTitleClassName}
            >
              <Input
                {...mobileInputClassProps}
                type="text"
                value={filterParameters.customerNumber || ''}
                onChange={(e) => handleFieldChange('customerNumber', e.target.value)}
              />
            </FormRow>
          </div>
        </div>

        <div
          className={clsx(
            'shrink-0',
            userAgent.isDesktop
              ? 'flex min-w-[100px] items-center border-t border-gray-300 px-3 pt-4 min-[1150px]:self-stretch min-[1150px]:border-l min-[1150px]:border-t-0 min-[1150px]:pt-0'
              : 'flex w-auto items-center self-stretch border-l border-gray-300 px-2'
          )}
        >
          <Button title={t('wf.searchHeader.search')} onClick={handleSearch} />
        </div>
      </div>

      {showNoResults && hasSearched && availableWorkplaces.length === 0 && (
        <h3 className="pt-4 text-center text-base">{t('wf.common.noResults')}</h3>
      )}
    </div>
  );
}

export default WF_MapSearchHeader;
